import React, { useState, useEffect } from 'react'
import { Users, Search, Mail, Phone, Calendar, Shield, Edit2, Trash2, UserX, UserCheck, AlertTriangle } from 'lucide-react'
import { supabase, SecretUser } from '../../lib/supabase'
import LoadingSpinner from '../ui/LoadingSpinner'
import EditUserModal from './EditUserModal'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../ui/EnterpriseToast'

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<SecretUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<'all' | 'admin' | 'user'>('all')
  const [editingUser, setEditingUser] = useState<SecretUser | null>(null)
  const [deletingUser, setDeletingUser] = useState<SecretUser | null>(null)

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)

      // Get all users
      const { data: usersData, error: usersError } = await supabase
        .from('secret_users')
        .select('*')
        .order('created_at', { ascending: false })

      if (usersError) throw usersError

      // Get admin users
      const { data: adminData, error: adminError } = await supabase
        .from('secret_admin_users')
        .select('user_id')

      if (adminError) throw adminError

      // Mark which users are admins
      const adminIds = new Set(adminData?.map(a => a.user_id) || [])
      const enrichedUsers = usersData?.map(user => ({
        ...user,
        is_admin: adminIds.has(user.id)
      })) || []

      setUsers(enrichedUsers as any)
    } catch (error: any) {
      console.error('Failed to load users:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Loading Failed"
          message="Could not load users"
          type="error"
          visible={t.visible}
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (user: SecretUser) => {
    try {
      // Soft delete: mark user as inactive
      const { error: updateError } = await supabase
        .from('secret_users')
        .update({
          is_active: false,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id)

      if (updateError) throw updateError

      // Create audit log
      await supabase
        .from('secret_audit_logs')
        .insert({
          event_type: 'admin_action',
          severity: 'warning',
          action: 'user_deactivated',
          user_id: user.id,
          metadata: {
            deactivated_by: 'admin_user',
            user_email: user.email,
          },
        })

      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="User Deactivated"
          message="The user account has been deactivated"
          type="success"
          visible={t.visible}
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
      setDeletingUser(null)
      loadUsers()

    } catch (error: any) {
      console.error('Failed to delete user:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          title="Deactivation Failed"
          message={error.message || 'Failed to deactivate user'}
          type="error"
          visible={t.visible}
          onDismiss={() => toast.dismiss(t.id)}
        />
      ))
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.phone?.includes(searchTerm)) ||
      (user.first_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()))

    return matchesSearch
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center space-x-3">
            <Users size={32} className="text-purple-600" />
            <span>User Management</span>
          </h1>
          <p className="text-muted-foreground mt-2">Manage SECRET members and their accounts</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <Users className="text-purple-600" size={20} />
            <span className="text-2xl font-bold text-foreground">{users.length}</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Total Users</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <Mail className="text-blue-600" size={20} />
            <span className="text-2xl font-bold text-foreground">
              {users.filter(u => u.email_verified).length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Email Verified</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <Phone className="text-green-600" size={20} />
            <span className="text-2xl font-bold text-foreground">
              {users.filter(u => u.phone_verified).length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Phone Verified</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <Shield className="text-orange-600" size={20} />
            <span className="text-2xl font-bold text-foreground">
              {users.filter(u => u.is_admin).length}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Admins</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* User Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-foreground">User</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-foreground">Contact</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-foreground">Status</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-foreground">Role</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-foreground">Joined</th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold">
                            {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-foreground">
                            {user.first_name && user.last_name
                              ? `${user.first_name} ${user.last_name}`
                              : user.first_name || user.last_name || 'No name'}
                          </div>
                          <div className="text-sm text-muted-foreground">ID: {user.id.slice(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {user.email && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Mail size={14} className="text-muted-foreground" />
                            <span className="text-foreground">{user.email}</span>
                          </div>
                        )}
                        {user.phone && (
                          <div className="flex items-center space-x-2 text-sm">
                            <Phone size={14} className="text-muted-foreground" />
                            <span className="text-foreground">{user.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {user.email_verified && (
                          <div className="flex items-center space-x-1 text-xs text-green-600">
                            <UserCheck size={14} />
                            <span>Email verified</span>
                          </div>
                        )}
                        {user.phone_verified && (
                          <div className="flex items-center space-x-1 text-xs text-green-600">
                            <UserCheck size={14} />
                            <span>Phone verified</span>
                          </div>
                        )}
                        {!user.email_verified && !user.phone_verified && (
                          <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                            <UserX size={14} />
                            <span>Not verified</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_admin ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                          <Shield size={12} className="mr-1" />
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
                          User
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        <Calendar size={14} />
                        <span>
                          {new Date(user.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                          title="Edit user"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeletingUser(user)}
                          className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          title="Deactivate user"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={loadUsers}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-foreground mb-2">
                  Deactivate User Account
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to deactivate{' '}
                  <strong className="text-foreground">
                    {deletingUser.first_name && deletingUser.last_name
                      ? `${deletingUser.first_name} ${deletingUser.last_name}`
                      : deletingUser.email}
                  </strong>
                  ? This will prevent them from accessing their account, but their data will be preserved.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setDeletingUser(null)}
                    className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteUser(deletingUser)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
                  >
                    Deactivate Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
